import ScheduleQuiz from "../../../../../assets/icons/new-note.svg";
import BulkQuestionIcon from "../../../../../assets/icons/bulk-question-icon.svg";
import AIIcon from "../../../../../assets/icons/magic-wand.svg";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { ChatBubbleOutline, DeleteOutline, FiberManualRecord } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomOverviewCard from "../../../../../customComponents/CustomOverviewCard";
import QuestionListCard from "../../../../customComponents/QuestionListCard";
import { useGetQuestionsQuery, useGetTeacherSubjectsQuery, useGetSchoolQuestionsQuery, useDeleteQuestionMutation } from "../../../../../app/questionApiSlice";
import { selectCurrentUser } from "../../../auth/authSlice";
import React from "react";
import { useGetSubjectsPaginatedQuery } from "../../../subjects/subjectsApiSlice";
import { useGetClassesPaginatedQuery } from "../../../classroom/classApiSlice";

const overviewCard = [
  { id: 1, icon: ScheduleQuiz, text: "Add Question", link: "new" },
  { id: 2, icon: BulkQuestionIcon, text: "Bulk Questions", link: "bulk" },
  { id: 3, icon: AIIcon, text: "AI Questions", link: "ai" },
];

const Questions = () => {
  const [sortBy, setSortBy] = React.useState("date");
  const [page, setPage] = React.useState(1);
  const [filterStatus, setFilterStatus] = React.useState("all"); // all, approved, pending
  const [filterSubject, setFilterSubject] = React.useState("all");
  const [filterLevel, setFilterLevel] = React.useState("all");
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const [deleteQuestion] = useDeleteQuestionMutation();
  const userRoles = currentUser?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isAdmin = userRoles.includes("admin") && !userRoles.includes("teacher");
  const isReviewer = userRoles.includes("reviewer") && !userRoles.includes("teacher");
  const useSchoolQuestions = isAdmin || isReviewer;
  
  // Fetch teacher subjects — skip for admin/reviewer-only users
  const { data: teacherSubjectsData } = useGetTeacherSubjectsQuery(undefined, { skip: useSchoolQuestions });
  // Fetch school-wide subjects and levels for admin/reviewer
  const { data: subjectsData } = useGetSubjectsPaginatedQuery({ page: 1, per_page: 100 }, { skip: !useSchoolQuestions });
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 100 }, { skip: !useSchoolQuestions });
  
  // Determine which query to use
  const { data: myQuestionsData, isLoading: myLoading, isFetching: myFetching } = useGetQuestionsQuery({ 
    page, 
    per_page: itemsPerPage,
    is_approved: filterStatus === "all" ? null : filterStatus === "approved",
    subject: filterSubject !== "all" ? filterSubject : null,
    level: filterLevel !== "all" ? filterLevel : null,
    sort_by: sortBy,
  }, { skip: useSchoolQuestions });

  const { data: schoolQuestionsData, isLoading: schoolLoading, isFetching: schoolFetching } = useGetSchoolQuestionsQuery({
    page,
    per_page: itemsPerPage,
    is_approved: filterStatus === "all" ? null : filterStatus === "approved",
    subject: filterSubject !== "all" ? filterSubject : null,
    level: filterLevel !== "all" ? filterLevel : null,
    sort_by: sortBy,
  }, { skip: !useSchoolQuestions });

  const data = useSchoolQuestions ? schoolQuestionsData : myQuestionsData;
  const isLoading = useSchoolQuestions ? schoolLoading : myLoading;
  const isFetching = useSchoolQuestions ? schoolFetching : myFetching;

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date string");
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Sort filtered results (server handles sorting)
  const sortedQuestions = data?.questions || [];

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handleFilterStatusChange = (event) => {
    setFilterStatus(event.target.value);
    setPage(1);
  };

  const handleFilterSubjectChange = (event) => {
    setFilterSubject(event.target.value);
    setPage(1);
  };

  const handleFilterLevelChange = (event) => {
    setFilterLevel(event.target.value);
    setPage(1);
  };

  const totalQuestions = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.pages || 0;

  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalQuestions);

  // ── Selection helpers ──────────────────────────────────────────────────────
  const allPageIds = sortedQuestions.map((q) => q.id);
  const allPageSelected =
    allPageIds.length > 0 && allPageIds.every((id) => selectedIds.has(id));
  const somePageSelected = allPageIds.some((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allPageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allPageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const handleDeleteConfirmed = async () => {
    setIsDeleting(true);
    try {
      await Promise.all([...selectedIds].map((id) => deleteQuestion(id)));
      setSelectedIds(new Set());
    } finally {
      setIsDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  // Get unique subjects and levels from teacher's subjects or school-wide data
  const uniqueSubjects = React.useMemo(() => {
    if (useSchoolQuestions) {
      if (!subjectsData?.subjects) return [];
      return [...new Set(subjectsData.subjects.map((s) => s.name).filter(Boolean))].sort();
    }
    if (!teacherSubjectsData) return [];
    // Extract unique subjects from classrooms
    const subjects = teacherSubjectsData.map((classroom) => classroom.name);
    return [...new Set(subjects)].filter(Boolean).sort();
  }, [useSchoolQuestions, teacherSubjectsData, subjectsData]);

  const uniqueLevels = React.useMemo(() => {
    if (useSchoolQuestions) {
      if (!classroomsData?.classrooms) return [];
      const levels = classroomsData.classrooms.map((c) => c.level);
      return [...new Set(levels)].filter(Boolean).sort();
    }
    if (!teacherSubjectsData) return [];
    // Extract unique levels from classrooms
    const levels = teacherSubjectsData.map((classroom) => classroom.level);
    return [...new Set(levels)].filter(Boolean).sort();
  }, [useSchoolQuestions, teacherSubjectsData, classroomsData]);

  return (
    <Box>
      {!isAdmin && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "auto",
              sm: "auto",
              md: "auto auto auto",
              lg: "auto auto auto",
            },
            gap: { xs: 2.5, sm: 2, md: 2, lg: 2.5 },
          }}
        >
          {overviewCard.map((card) => (
            <CustomOverviewCard
              key={card.id}
              title={card.text}
              icon={card.icon}
              onClick={() => navigate(`${card.link}`)}
            />
          ))}
        </Box>
      )}
      <Box sx={{ marginTop: "2em" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1em",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={allPageSelected}
              indeterminate={!allPageSelected && somePageSelected}
              onChange={toggleSelectAll}
              disabled={allPageIds.length === 0}
              size="small"
              sx={{ p: 0.5 }}
            />
            {selectedIds.size > 0 ? (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteOutline />}
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={isDeleting}
                sx={{ borderRadius: 2 }}
              >
                Delete {selectedIds.size} selected
              </Button>
            ) : (
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: "1rem", md: "1.25rem" },
                  fontWeight: 700,
                  letterSpacing: "-1px",
                }}
              >
                Recent Questions
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Select
              value={filterStatus}
              onChange={handleFilterStatusChange}
              size="small"
              displayEmpty
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
            <Select
              value={filterSubject}
              onChange={handleFilterSubjectChange}
              size="small"
              displayEmpty
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">All Subjects</MenuItem>
              {uniqueSubjects.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
            <Select
              value={filterLevel}
              onChange={handleFilterLevelChange}
              size="small"
              displayEmpty
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">All Levels</MenuItem>
              {uniqueLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
            <Select
              value={sortBy}
              onChange={handleChangeSort}
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="date">Sort by: Date</MenuItem>
              <MenuItem value="subject">Sort by: Subject</MenuItem>
              <MenuItem value="level">Sort by: Level</MenuItem>
            </Select>
          </Box>
        </Box>

        {(isLoading || isFetching) && (
          <Box sx={{ width: "100%", mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {sortedQuestions.map((question) => {
          const reviewCount = question.reviews?.length || 0;
          const activeReviews = question.reviews?.filter((r) => !r.is_deleted) || [];
          const latestReview = activeReviews.length
            ? activeReviews.reduce((a, b) =>
                new Date(a.created_at) > new Date(b.created_at) ? a : b
              )
            : null;
          const needsResponse = latestReview && latestReview.user_id !== currentUser?.id;
          const isSelected = selectedIds.has(question.id);

          return (
            <Box
              key={question.id}
              sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
            >
              <Checkbox
                checked={isSelected}
                onChange={() => toggleSelect(question.id)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ mt: 1.5, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1 }}>
                <QuestionListCard
                  question={question}
                  onClick={
                    !isAdmin && !isSelected
                      ? () => navigate(`edit/${question.id}`, { state: { cardData: question } })
                      : () => toggleSelect(question.id)
                  }
                  formatDate={formatDate}
                  topBadge={(reviewCount > 0 || needsResponse) && (
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      {reviewCount > 0 && (
                        <Chip
                          icon={<ChatBubbleOutline sx={{ fontSize: 14 }} />}
                          label={`${reviewCount} review${reviewCount !== 1 ? "s" : ""}`}
                          size="small"
                          sx={(theme) => ({ fontSize: "0.75rem", height: 24, backgroundColor: theme.palette.mode === "dark" ? "#1a2a4a" : "#E8F0FE", color: "primary.safeOnLight", "& .MuiChip-icon": { color: "primary.safeOnLight" } })}
                        />
                      )}
                      {needsResponse && (
                        <Chip
                          icon={<FiberManualRecord sx={{ fontSize: 10 }} />}
                          label="Needs your response"
                          size="small"
                          sx={(theme) => ({ fontSize: "0.75rem", height: 24, backgroundColor: theme.palette.mode === "dark" ? "#3a2000" : "#FFF3E0", color: theme.palette.mode === "dark" ? "#FFA726" : "#E65100", "& .MuiChip-icon": { color: theme.palette.mode === "dark" ? "#FFA726" : "#E65100" } })}
                        />
                      )}
                    </Box>
                  )}
                />
              </Box>
            </Box>
          );
        })}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "2em",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex} to {endIndex} of {totalQuestions} entries
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
            sx={{ color: "#CCCCCC" }}
          />
        </Box>
      </Box>

      {/* Bulk delete confirmation dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => !isDeleting && setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete {selectedIds.size} question{selectedIds.size !== 1 ? "s" : ""}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete {selectedIds.size} selected question{selectedIds.size !== 1 ? "s" : ""}.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirmed}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Questions;
